/**
 * Copyright (c) Simam Digital.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as THREE from 'three';

/**
 * Layout of the shared world's plot grid. Each plot is a fixed cell; a player's
 * plotIndex maps deterministically to a world position via plotOffset().
 *
 * The world is one bounded island, so the grid is grown as an extended procedural
 * ground (see PlotGroundSystem): the home island sits at cell 0 and the surrounding
 * cells are flat walkable ground. ROWS caps how much ground we build up front.
 */
export const PLOT_GRID = {
	COLUMNS: 8, // plots per row before wrapping to the next row
	ROWS: 8, // rows of ground built up front (COLUMNS * ROWS = capacity)
	SPACING: 10, // meters between plot centers
	ORIGIN: new THREE.Vector3(0, 0, 0),
};

/**
 * Deterministic world position for a given plot index.
 * @param {number} plotIndex
 * @param {THREE.Vector3} [target] optional vector to write into (avoids allocation)
 * @returns {THREE.Vector3}
 */
export function plotOffset(plotIndex, target = new THREE.Vector3()) {
	const index = plotIndex > 0 ? plotIndex : 0;
	const col = index % PLOT_GRID.COLUMNS;
	const row = Math.floor(index / PLOT_GRID.COLUMNS);
	return target.set(
		PLOT_GRID.ORIGIN.x + col * PLOT_GRID.SPACING,
		PLOT_GRID.ORIGIN.y,
		PLOT_GRID.ORIGIN.z + row * PLOT_GRID.SPACING,
	);
}

/**
 * The world-space spawn point for a plot: the map's canonical start position
 * shifted into that plot's cell. Cell 0 (the home island) resolves to the map's
 * own Player_Start, so single-player / unconfigured worlds are unaffected.
 * @param {number} plotIndex
 * @param {THREE.Vector3} baseInitialPosition the map's INITIAL_POSITION (foot pos)
 * @param {THREE.Vector3} [target]
 * @returns {THREE.Vector3}
 */
export function plotSpawnPosition(
	plotIndex,
	baseInitialPosition,
	target = new THREE.Vector3(),
) {
	plotOffset(plotIndex, target);
	target.x += baseInitialPosition.x;
	target.y = baseInitialPosition.y;
	target.z += baseInitialPosition.z;
	return target;
}

/**
 * The extent of the grid ground built up front, in meters, as { min, max } on the
 * XZ plane (y is the ground height, supplied separately). Padded by one cell so the
 * edge plots have ground under their whole footprint.
 * @returns {{ minX:number, maxX:number, minZ:number, maxZ:number }}
 */
export function gridGroundExtent() {
	const pad = PLOT_GRID.SPACING;
	return {
		minX: PLOT_GRID.ORIGIN.x - pad,
		maxX: PLOT_GRID.ORIGIN.x + (PLOT_GRID.COLUMNS - 1) * PLOT_GRID.SPACING + pad,
		minZ: PLOT_GRID.ORIGIN.z - pad,
		maxZ: PLOT_GRID.ORIGIN.z + (PLOT_GRID.ROWS - 1) * PLOT_GRID.SPACING + pad,
	};
}
